import React, { useState, useEffect } from 'react';
import type { FC, FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router';

import { LoginLinkAPIs, TokenTypes } from '@/api';
import { Button, ButtonSize, ButtonType } from '@/common/button';
import { Carousel } from '@/common/carousel';
import { CarouselSlide } from '@/common/carousel-slide';
import { CustomToast } from '@/common/custom-toast';
import { IntaglioLogo } from '@/common/intaglio-logo';
import animData1 from '@/common/lottie/slide-1.json';
import animData2 from '@/common/lottie/slide-2.json';
import animData3 from '@/common/lottie/slide-3.json';
import { TextField, TextFieldType } from '@/common/text-field';
import { useAuth } from '@/hooks/useAuth';

import { GoogleSigninButton } from './google_signin_button';

export const Login: FC = () => {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [token] = useState(searchParams.get('token') || null);
    const [emailSent, setEmailSent] = useState(false);
    const [emailValue, setEmailValue] = useState('');
    const [emailHasError, setEmailHasError] = useState(false);

    useEffect(() => {
        if (token) {
            const tokenLogin = async (token: string) => {
                try {
                    await login(token, TokenTypes.LOGIN_LINK);
                    await navigate('/templates');
                } catch (e) {
                    CustomToast.error('Unable to login with token');
                    console.error(e);
                }
            };
            tokenLogin(token);
        }
    }, [token, navigate, login]);

    const handleLoginLink = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault();

        // validte email using regex
        const emailRegex =
            '^(?:[a-zA-Z0-9!#$%&\'*+/=?^_`{|}~-]+(?:.[a-zA-Z0-9!#$%&\'*+/=?^_`{|}~-]+)*|"(?:[\\x01-\\x08\\x0b\\x0c\\x0e-\\x1f\\x21\\x23-\\x5b\\x5d-\\x7f]|\\\\[\\x01-\\x09\\x0b\\x0c\\x0e-\\x7f])*")@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$';

        if (!emailValue || RegExp(emailRegex).test(emailValue) === false) {
            setEmailHasError(true);
            return;
        }

        try {
            await LoginLinkAPIs.sendLoginLink(emailValue);
        } catch (error) {
            return;
        } finally {
            setEmailSent(true);
        }
    };

    return (
        <div className="min-h-screen flex flex-row bg-black-50">
            <div className="md:w-2/5 w-full flex flex-col px-24 py-16">
                <div className="w-[400px] h-full flex flex-col justify-evenly">
                    <IntaglioLogo className="mt-6" />
                    {emailSent && (
                        <div className="h-full w-full flex flex-col justify-center">
                            <p className="text-white text-xl">Check your email!</p>
                            <p className="text-gray-40 text-sm font-normal">
                                You will receive a login link on <span className="text-white">{emailValue}</span> if
                                it's connected to Intaglio. Click the link to log in.
                            </p>
                            <Button
                                title="Try Again"
                                className="mt-4"
                                buttonType={ButtonType.SECONDARY}
                                buttonSize={ButtonSize.LARGE}
                                onClick={() => {
                                    setEmailValue('');
                                    setEmailSent(false);
                                }}
                            />
                        </div>
                    )}
                    {!emailSent && (
                        <div className="h-full w-full items-center justify-center flex flex-col gap-5">
                            <p className="text-lg text-white font-normal w-[400px]">
                                Sign in using your work email, or your Google account connected to Intaglio.
                            </p>
                            <GoogleSigninButton
                                onSignIn={async (creds) => {
                                    try {
                                        await login(creds.credential, TokenTypes.GOOGLE);
                                        await navigate('/templates');
                                    } catch (e) {
                                        CustomToast.error('Unable to login with Google');
                                        console.error(e);
                                    }
                                }}
                            />
                            <div className="flex flex-row items-center mt-2 w-[400px]">
                                <hr className="bg-gray-100 border-gray-100 w-full" />
                                <p className="text-xs text-gray-110 mx-2">Or</p>
                                <hr className="bg-gray-100 border-gray-100 w-[400px]" />
                            </div>
                            <form onSubmit={handleLoginLink} className="flex flex-col w-[400px] gap-2">
                                <div className="w-full">
                                    <label className="block text-white text-sm font-normal mb-2" htmlFor="emailField">
                                        Email Address
                                    </label>
                                    <TextField
                                        id="emailField"
                                        type={TextFieldType.Email}
                                        value={emailValue}
                                        required={true}
                                        onChange={(value) => {
                                            if (emailHasError) setEmailHasError(false);
                                            setEmailValue(value);
                                        }}
                                        placeholder="Enter Email"
                                        hasError={emailHasError}
                                        errorMessage="Please enter a valid email"
                                    />
                                </div>
                                <div className="flex items-center justify-between mt-2 w-full">
                                    <Button
                                        type="submit"
                                        title="Get Login Link"
                                        buttonType={ButtonType.PRIMARY}
                                        buttonSize={ButtonSize.LARGE}
                                        className="w-full"
                                    />
                                </div>
                            </form>
                        </div>
                    )}
                </div>
            </div>
            <div className="w-3/5 gradient-background hidden md:block">
                <div className="min-h-screen flex items-center justify-center">
                    <Carousel
                        autoPlay={true}
                        interval={3000}
                        slideTexts={[
                            'Templatize designs from any platform',
                            'Set design rules that cannot be broken',
                            'Review and replace assets when needed',
                        ]}
                    >
                        <CarouselSlide key={'slide1'} animData={animData1} />
                        <CarouselSlide key={'slide2'} animData={animData2} />
                        <CarouselSlide key={'slide3'} animData={animData3} />
                    </Carousel>
                </div>
            </div>
        </div>
    );
};
